"use client";
import React, { ReactNode, useEffect, useRef } from "react";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { SCROLL_STAGGER_ITEM_SELECTOR } from "./constants";

// Reveal an item once its top edge passes 90% of the viewport height.
// A margin instead of a ratio threshold also works for items taller than the viewport.
const DEFAULT_ROOT_MARGIN = "0px 0px -10% 0px";
const IN_VIEW_CLASS = "in-view";

interface ScrollStaggerGroupProps {
  children: ReactNode;
  className?: string;
  staggerDelay?: number;
  rootMargin?: string;
}

/**
 * ScrollStaggerGroup Component
 *
 * Fades in every descendant marked with `SCROLL_STAGGER_ITEM_CLASS` as it scrolls into view.
 * Items entering the viewport together (initial load, anchor jump, fast scroll) are queued
 * in DOM order, each starting `staggerDelay` ms after the previous one — so the order holds
 * across nested containers (e.g. an intro block followed by a list).
 *
 * @example
 * ```tsx
 * <ScrollStaggerGroup>
 *   <h2 className={SCROLL_STAGGER_ITEM_CLASS}>Title</h2>
 *   <ul>
 *     <li className={SCROLL_STAGGER_ITEM_CLASS}>Item 1</li>
 *     <li className={SCROLL_STAGGER_ITEM_CLASS}>Item 2</li>
 *   </ul>
 * </ScrollStaggerGroup>
 * ```
 */
export const ScrollStaggerGroup = ({
  children,
  className,
  staggerDelay = 150,
  rootMargin = DEFAULT_ROOT_MARGIN,
}: ScrollStaggerGroupProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const items = Array.from(
      container.querySelectorAll<HTMLElement>(
        `${SCROLL_STAGGER_ITEM_SELECTOR}:not(.${IN_VIEW_CLASS})`,
      ),
    );

    const reveal = (item: HTMLElement, delay: number) => {
      item.style.animationDelay = `${delay}ms`;
      item.classList.add(IN_VIEW_CLASS);
    };

    if (prefersReducedMotion) {
      items.forEach((item) => reveal(item, 0));
      return;
    }

    const domIndex = new Map<Element, number>(
      items.map((item, index) => [item, index]),
    );
    // start time of the next reveal: keeps DOM order even when items enter in separate batches
    let nextStart = 0;

    const observer = new IntersectionObserver(
      (entries) => {
        const now = performance.now();

        entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => domIndex.get(entry.target))
          .filter((index): index is number => index !== undefined)
          .sort((a, b) => a - b)
          .forEach((index) => {
            const item = items[index];
            const start = Math.max(now, nextStart);
            nextStart = start + staggerDelay;

            observer.unobserve(item);
            reveal(item, start - now);
          });
      },
      { rootMargin },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [prefersReducedMotion, staggerDelay, rootMargin]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};
